function gMaps() {
    return Hop.window.google && Hop.window.google.maps;
}

function getLongName(addresses, type, defaultValue = '') {
    if (!addresses) {
        return defaultValue;
    }
    return addresses.reduce((result, address) => (address.types.includes(type) ? address.long_name : result), defaultValue);
}

function getShortName(addresses, type, defaultValue = '') {
    if (!addresses) {
        return defaultValue;
    }
    return addresses.reduce((result, address) => (address.types.includes(type) ? address.short_name : result), defaultValue);
}

export class GmapPlaces {
    constructor(root, {id, ajax = false, submit = true}) {
        this.id = id;
        this.root = root;
        this.input = this.root.querySelector('input[name=location]') || this.root.querySelector('input[name="address.formattedAddress"]');

        // let's be nice with dynamic templates (JSP, JS,...) and don't force them to conditionally include this module
        if (this.input === null) {
            return;
        }

        this.inputCity = this.root.querySelector('input[name=city]') || this.root.querySelector('input[name="address.city"]');
        this.inputCountryCode = this.root.querySelector('input[name=countryCode]') || this.root.querySelector('input[name="address.countryCode"]');
        this.inputCountry = this.root.querySelector('input[name=country]') || this.root.querySelector('input[name="address.country"]');

        this.administrativeAreaLevel1 =
            this.root.querySelector('input[name=administrativeAreaLevel1]')
            || this.root.querySelector('input[name="address.administrativeAreaLevel1"]');
        this.administrativeAreaLevel1Code =
            this.root.querySelector('input[name=administrativeAreaLevel1Code]')
            || this.root.querySelector('input[name="address.administrativeAreaLevel1Code"]');
        this.administrativeAreaLevel2 = this.root.querySelector(
            'input[name=administrativeAreaLevel2]' || this.root.querySelector('input[name="address.administrativeAreaLevel2"]'),
        );
        this.administrativeAreaLevel2Code =
            this.root.querySelector('input[name=administrativeAreaLevel2Code]')
            || this.root.querySelector('input[name="address.administrativeAreaLevel2Code"]');
        this.administrativeAreaLevel3 =
            this.root.querySelector('input[name=administrativeAreaLevel3]')
            || this.root.querySelector('input[name="address.administrativeAreaLevel3"]');
        this.administrativeAreaLevel3Code =
            this.root.querySelector('input[name=administrativeAreaLevel3Code]')
            || this.root.querySelector('input[name="address.administrativeAreaLevel3Code"]');
        this.administrativeAreaLevel4 =
            this.root.querySelector('input[name=administrativeAreaLevel4]')
            || this.root.querySelector('input[name="address.administrativeAreaLevel4"]');
        this.administrativeAreaLevel4Code =
            this.root.querySelector('input[name=administrativeAreaLevel4Code]')
            || this.root.querySelector('input[name="address.administrativeAreaLevel4Code"]');

        this.inputLong = this.root.querySelector('input[name=lon]') || this.root.querySelector('input[name="address.loc.lon"]');
        this.inputLat = this.root.querySelector('input[name=lat]') || this.root.querySelector('input[name="address.loc.lat"]');
        this.isAjaxContext = ajax;
        this.isToSubmit = submit;
        this.place = null;
        this.defaultLocationValue = this.input.value;
        this.isGoogleMapEventChange = false;

        this.options = {types: ['(regions)']};

        if (typeof gMaps() === 'object') {
            this.initGmap();
        } else {
            Hop.document.addEventListener('gmapReady', () => this.initGmap());
        }
        this.input.addEventListener('focus', () => this.initGmap());

        setTimeout(() => !this.autocomplete && this.root && this.root.classList.add('fadeout'), 2000);
    }

    destroy() {
        this.input.removeEventListener('blur', this.inputBlurCallbackListener);
        this.root.removeEventListener('submit', this.inputSubmitCallbackListener);

        try {
            gMaps().event.clearListeners(this.autocomplete, 'place_changed');
        } finally {
            this.autocomplete = null;
            this.place = null;
            // eslint-disable-next-line guard-for-in
            for (const prop in this) {
                this[prop] !== null && this[prop].selector && delete this[prop];
            }
        }
    }

    initGmap() {
        if (this.autocomplete) {
            return;
        }

        if (!gMaps() || !gMaps().event || !gMaps().places) {
            // google.maps not loaded yet, will retry in 1 second or at next focus
            setTimeout(() => this.initGmap(), 1000);
            return;
        }

        this.autocomplete = new (gMaps().places.Autocomplete)(this.input, this.options);
        this.root.classList.add('fadeout');

        this.setHandlers();
    }

    setHandlers() {
        let isEnter = false;

        gMaps().event.addListener(this.autocomplete, 'place_changed', () => {
            this.isGoogleMapEventChange = true;
            this.place = this.autocomplete.getPlace();

            if (!isEnter) {
                if (!this.place.geometry) {
                    this.place = null;
                    this.resetLatLon(
                        this.inputCity,
                        this.inputCountryCode,
                        this.inputCountry,
                        this.administrativeAreaLevel1,
                        this.administrativeAreaLevel1Code,
                        this.administrativeAreaLevel2,
                        this.administrativeAreaLevel2Code,
                        this.administrativeAreaLevel3,
                        this.administrativeAreaLevel3Code,
                        this.administrativeAreaLevel4,
                        this.administrativeAreaLevel4Code,
                        this.inputLat,
                        this.inputLong,
                    );

                    this.tryGeocoding(() => {
                        this.isGoogleMapEventChange = false;
                        this.submitForm();
                    });
                } else {
                    this.setForm(this.place);
                    this.isGoogleMapEventChange = false;
                }
                isEnter = false;
            } else {
                this.isGoogleMapEventChange = false;
            }

            Hop.events.trigger('location:place_changed', this.place);
        });

        this.input.addEventListener('keydown', (e) => {
            // if user press ENTER
            if (e.keyCode === 13) {
                e.preventDefault();
                isEnter = true;
                this.tryGeocoding(() => {
                    this.submitForm();
                    isEnter = false;
                });
            }
        });

        if (this.isAjaxContext) {
            this.input.addEventListener('blur', this.inputBlurCallbackListener.bind(this));
        } else {
            this.root.addEventListener('submit', this.inputSubmitCallbackListener.bind(this));
        }
    }

    inputBlurCallbackListener() {
        setTimeout(() => {
            if (!this.input) {
                return;
            }

            if (this.input.value && !this.isGoogleMapEventChange && this.defaultLocationValue !== this.input.value) {
                this.tryGeocoding(() => this.cleanUrl());
            } else if (!this.input.value) {
                this.resetAllLocalFields();
            }
        }, 50);
    }

    inputSubmitCallbackListener(e) {
        if (!this.input || !this.inputLat || !this.inputCountryCode) {
            return;
        }

        if (
            (this.input.value && !this.inputLat.value && !this.inputCountryCode.value)
            || (this.input.value && this.defaultLocationValue && this.defaultLocationValue !== this.input.value)
        ) {
            e.preventDefault();
            this.tryGeocoding(() => this.submitForm());
        } else if (!this.input.value && this.inputLat.value) {
            this.resetAllLocalFields(true);
        }
    }

    cleanUrl() {
        [
            this.input,
            this.inputCity,
            this.inputCountryCode,
            this.inputCountry,
            this.administrativeAreaLevel1,
            this.administrativeAreaLevel1Code,
            this.administrativeAreaLevel2,
            this.administrativeAreaLevel2Code,
            this.administrativeAreaLevel3,
            this.administrativeAreaLevel3Code,
            this.administrativeAreaLevel4,
            this.administrativeAreaLevel4Code,
            this.inputLong,
            this.inputLat,
        ].forEach((input) => input && (input.disabled = !input.value));
    }

    submitForm() {
        if (this.isToSubmit && (typeof this.root.checkValidity !== 'function' || this.root.checkValidity())) {
            if (this.isAjaxContext) {
                this.cleanUrl();
                this.root.querySelector('button[type=submit]').click();
            } else {
                this.cleanUrl();
                this.root.submit();
            }
        }
    }

    _geocoder(address) {
        const service = new (gMaps().places.AutocompleteService)();
        const googlePlacesService = new (gMaps().places.PlacesService)(this.input);

        return new Promise((resolve, reject) =>
            service.getPlacePredictions({input: address, types: ['(regions)']}, (predictions, status) => {
                if (status === gMaps().places.PlacesServiceStatus.OK) {
                    if (predictions && predictions.length) {
                        const reference = predictions[0].reference;
                        googlePlacesService.getDetails({reference}, (details, status) => (details ? resolve(details) : reject(new Error(status))));
                    } else {
                        resolve(null);
                    }
                } else {
                    reject(new Error(status));
                }
            }),
        );
    }

    tryGeocoding(callback) {
        const address = this.input.value;

        if (address.length > 1) {
            // try to call geocoding manually
            this._geocoder(address).then(
                (results) => {
                    if (results) {
                        this.input && this.setForm(results);
                    }
                    callback && callback(address);
                },
                () => {
                    this.resetAllLocalFields();
                    callback && callback(address);
                },
            );
        } else {
            this.resetAllLocalFields();
            callback && callback(address);
        }
    }

    resetAllLocalFields(cleanUrl) {
        this.resetLatLon(
            cleanUrl,
            this.input,
            this.inputCity,
            this.inputCountryCode,
            this.inputCountry,
            this.administrativeAreaLevel1,
            this.administrativeAreaLevel1Code,
            this.administrativeAreaLevel2,
            this.administrativeAreaLevel2Code,
            this.administrativeAreaLevel3,
            this.administrativeAreaLevel3Code,
            this.administrativeAreaLevel4,
            this.administrativeAreaLevel4Code,
            this.inputLat,
            this.inputLong,
        );
    }

    setForm(results) {
        this.defaultLocationValue = results.formatted_address;
        this.input.value = results.formatted_address;
        this.inputLat.value = results.geometry.location.lat();
        this.inputLong.value = results.geometry.location.lng();
        this.inputCity.value = getLongName(results['address_components'], 'locality');
        this.inputCountryCode.value = getShortName(results['address_components'], 'country');
        this.inputCountry.value = getLongName(results['address_components'], 'country');
        this.administrativeAreaLevel1Code.value = getShortName(results['address_components'], 'administrative_area_level_1');
        this.administrativeAreaLevel1.value = getLongName(results['address_components'], 'administrative_area_level_1');
        this.administrativeAreaLevel2Code.value = getShortName(results['address_components'], 'administrative_area_level_2');
        this.administrativeAreaLevel2.value = getLongName(results['address_components'], 'administrative_area_level_2');
        this.administrativeAreaLevel3Code.value = getShortName(results['address_components'], 'administrative_area_level_3');
        this.administrativeAreaLevel3.value = getLongName(results['address_components'], 'administrative_area_level_3');
        this.administrativeAreaLevel4Code.value = getShortName(results['address_components'], 'administrative_area_level_4');
        this.administrativeAreaLevel4.value = getLongName(results['address_components'], 'administrative_area_level_4');

        // reset lat and lon if city is not given
        // in this case, we just search by zone (country, region, etc...)
        if (this.inputCity.value === '') {
            this.inputLat.value = '';
            this.inputLong.value = '';
        }

        this.input.focus();
    }

    resetLatLon(cleanUrl, ...inputs) {
        inputs.forEach((input) => {
            if (!input) {
                return;
            }
            input.value = '';
            cleanUrl && (input.disabled = true);
        });
    }
}
